import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { customerinvoicesApi } from "../../../api/customerinvoices";
import { fetchProjects } from "../../../api/project";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { ModuleFilter } from "../../../components/utils/ModuleFilter";
import { PMPagination } from "../../../components/utils/PMPagination";
import { toast } from "react-hot-toast";
import {
  ArrowPathRoundedSquareIcon,
  DocumentTextIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import { formatter, formattedDate } from "../../../components/Utils";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

export const CustomerInvoiceList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    query: "",
    projects: [],
  });
  const [projectOptions, setProjectOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [currentCount, setCurrentCount] = useState(0);

  useEffect(() => {
    fetchInvoices();
    fetchProjectsForFilter();
  }, []);

  useEffect(() => {
    fetchInvoices();
  }, [filters, page, perPage]);

  const fetchProjectsForFilter = async () => {
    try {
      const response = await fetchProjects({
        fields: "id,name",
        limit: 1000,
      });
      if (response?.success && response?.data) {
        const options = response.data.map((project) => ({
          value: project.id,
          label: project.name || `Prosjekt #${project.id}`,
        }));
        setProjectOptions(options);
      }
    } catch (error) {
      console.error("Error fetching projects for filter:", error);
    }
  };

  const fetchInvoices = async () => {
    try {
      setIsLoading(true);
      const params = {
        fields: "id,invoice_number,invoice_date,invoice_due_date,amount,amount_excluding_vat,vat,customer,project",
        page: page,
        per_page: perPage,
      };
      
      // If project_id is provided, use project-specific endpoint
      if (filters.projects && filters.projects.length === 1) {
        params.project_id = filters.projects[0];
      }
      
      const response = await customerinvoicesApi.getCustomerInvoices(params);
      if (response.data?.success) {
        setInvoices(response.data.data || []);
        setTotalCount(response.data.meta?.total_count || response.data.data?.length || 0);
        setCurrentCount(response.data.data?.length || 0);
      }
    } catch (error) {
      console.error("Error fetching customer invoices:", error);
      toast.error("Kunne ikke hente kundefakturaer");
    } finally {
      setIsLoading(false);
    }
  };

  const filteredInvoices = useMemo(() => {
    let filtered = invoices;

    // Filter by search query
    if (filters.query) {
      const query = filters.query.toLowerCase();
      filtered = filtered.filter(
        (invoice) =>
          invoice.invoice_number?.toLowerCase().includes(query) ||
          invoice.customer?.name?.toLowerCase().includes(query) ||
          invoice.project?.name?.toLowerCase().includes(query)
      );
    }

    // Filter by projects (if multiple projects selected, filter client-side)
    if (filters.projects && filters.projects.length > 1) {
      filtered = filtered.filter((invoice) => {
        if (invoice.project_id) {
          return filters.projects.includes(invoice.project_id);
        }
        return false;
      });
    }

    return filtered;
  }, [invoices, filters]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  return (
    <div className="flex flex-col h-full">
      <DashboardHeader
        title="Kundefakturaer"
        actions={[
          {
            component: (
              <button
                onClick={fetchInvoices}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <ArrowPathRoundedSquareIcon className="w-5 h-5" />
                Oppdater
              </button>
            ),
          },
        ]}
      />

      <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8 pb-24">
        <div className="mt-8">
          {/* ModuleFilter for search and filtering */}
          <div className="mb-6">
            <ModuleFilter
              onFiltersChange={setFilters}
              initialFilters={filters}
              searchPlaceholder="Søk etter fakturanummer, kunde eller prosjekt..."
              projectOptions={projectOptions}
            />
          </div>

        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Henter fakturaer...</p>
          </div>
        ) : filteredInvoices.length === 0 ? (
          <div className="text-center py-12">
            <DocumentTextIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Ingen fakturaer funnet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredInvoices.map((invoice) => (
              <div
                key={invoice.id}
                className="bg-white rounded-lg shadow-md border border-gray-200 hover:shadow-lg transition-shadow"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {invoice.invoice_number || `Faktura #${invoice.id}`}
                        </h3>
                        {invoice.project && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                            {invoice.project.name}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span>
                          <strong>Kunde:</strong> {invoice.customer?.name || "Ukjent"}
                        </span>
                        <span>
                          <strong>Fakturadato:</strong>{" "}
                          {formattedDate(invoice.invoice_date)}
                        </span>
                        {invoice.invoice_due_date && (
                          <span>
                            <strong>Forfallsdato:</strong>{" "}
                            {formattedDate(invoice.invoice_due_date)}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => navigate(`/dashboard/customer-invoices/${invoice.id}`)}
                      className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      title="Vis detaljer"
                    >
                      <EyeIcon className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Beløp ekskl. mva</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {formatter.format(invoice.amount_excluding_vat || 0)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">MVA</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {formatter.format(invoice.vat || 0)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Totalt</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {formatter.format(invoice.amount || 0)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        </div>

        {/* Pagination */}
        {!isLoading && filteredInvoices.length > 0 && (
          <div className="mt-8">
            <PMPagination
              currentCount={currentCount}
              totalCount={totalCount}
              page={page}
              onPageChange={handlePageChange}
              perPage={perPage}
              showCount={true}
              showPages={true}
              showNextPrev={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};

